import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import CategoryFilter from '../../components/ui/CategoryFilter';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const SignUp = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const { error } = await signUp(formData?.email, formData?.password, formData?.fullName);
      if (error) {
        setErrors({ general: error?.message || 'Failed to create account' });
      } else {
        navigate('/e-book-catalog');
      }
    } catch (error) {
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - StrategyShelf</title>
        <meta name="description" content="Create your StrategyShelf account to purchase and access business strategy eBooks." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <CategoryFilter />

        <main className="pt-32 pb-12 md:pb-16 lg:pb-20">
          <div className="max-w-md mx-auto px-4 md:px-6">
            <div className="bg-card rounded-lg border border-border p-6 md:p-8">
              <div className="text-center mb-6">
                <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
                  Create Account
                </h1>
                <p className="text-sm text-muted-foreground">
                  Join StrategyShelf to access premium eBooks
                </p>
              </div>

              {errors?.general && (
                <div className="bg-error/10 border border-error/20 rounded-md p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{errors?.general}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  label="Full Name"
                  placeholder="John Doe"
                  value={formData?.fullName}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  error={errors?.fullName}
                  required
                />

                <Input
                  type="email"
                  label="Email Address"
                  placeholder="your.email@example.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  required
                />

                <Input
                  type="password"
                  label="Password"
                  placeholder="Minimum 8 characters"
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  error={errors?.password}
                  required
                />

                <Input
                  type="password"
                  label="Confirm Password"
                  placeholder="Re-enter password"
                  value={formData?.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                  error={errors?.confirmPassword}
                  required
                />

                <div className="bg-muted/50 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Shield" size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">
                        Your data is secure
                      </p>
                      <p className="text-xs text-muted-foreground">
                        We use industry-standard encryption to protect your information
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  disabled={isLoading}
                  iconName={isLoading ? 'Loader2' : 'UserPlus'}
                  iconPosition="left"
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link
                    to="/sign-in"
                    className="text-primary font-medium hover:underline focus-ring rounded-md"
                  >
                    Sign In
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default SignUp;